import { motion } from 'framer-motion';
import { products } from '../data/companyData';
import Footer from '../components/Footer';

const ProductsPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  const getProductImage = (productName) => {
    const imageMap = {
      'Common Beans': '/images/AFRI OILS_5.jpg',
      'Raw Peanut Kernels': '/images/AFRI OILS_6.jpg',
      'Roasted & Blanched Nuts': '/images/AFRI OILS_7.jpg',
      'Peanut Butter': '/images/AFRI OILS_8.jpg'
    };
    return imageMap[productName] || '/images/AFRI OILS_9.jpg';
  };

  return (
    <div>
      <section className="pt-32 pb-20 bg-gradient-to-br from-tev-green to-tev-green-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl font-bold mb-6">Our Products</h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              High-quality agricultural products for local and international markets
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <img 
              src="/images/AFRI OILS_10.jpg" 
              alt="Products Showcase" 
              className="rounded-xl shadow-xl w-full h-96 object-cover"
            />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Semi-processed Products */}
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
            >
              <h2 className="text-3xl font-semibold text-gray-900 mb-8">Semi-processed Products</h2>
              <div className="space-y-6">
                {products.semiProcessed.map((product, index) => (
                  <motion.div
                    key={index}
                    className="bg-gray-50 p-6 rounded-lg flex items-center card-hover"
                    variants={itemVariants}
                    whileHover={{ x: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img 
                      src={getProductImage(product.name)}
                      alt={product.name}
                      className="w-24 h-24 rounded-lg object-cover mr-6"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900 text-xl mb-2">{product.name}</h3>
                      <p className="text-gray-600">{product.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Value Added Products */}
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
            >
              <h2 className="text-3xl font-semibold text-gray-900 mb-8">Value Added Products</h2>
              <div className="space-y-6">
                {products.valueAdded.map((product, index) => (
                  <motion.div
                    key={index}
                    className="bg-gray-50 p-6 rounded-lg flex items-center card-hover"
                    variants={itemVariants}
                    whileHover={{ x: 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img 
                      src={getProductImage(product.name)}
                      alt={product.name}
                      className="w-24 h-24 rounded-lg object-cover mr-6"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900 text-xl mb-2">{product.name}</h3>
                      <p className="text-gray-600">{product.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Product Gallery */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <h2 className="text-3xl font-semibold text-gray-900 mb-8 text-center">Product Gallery</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                '/images/AFRI OILS_11.jpg',
                '/images/AFRI OILS_12.jpg',
                '/images/AFRI OILS_13.jpg',
                '/images/AFRI OILS_14.jpg',
                '/images/AFRI OILS_15.jpg',
                '/images/AFRI OILS_16.jpg',
                '/images/AFRI OILS_17.jpg',
                '/images/AFRI OILS_18.jpg'
              ].map((image, index) => (
                <motion.div
                  key={index}
                  className="overflow-hidden rounded-lg shadow-md card-hover"
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img 
                    src={image}
                    alt={`Product ${index + 1}`}
                    className="w-full h-48 object-cover"
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Quality Assurance */}
          <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            className="mt-16 bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-xl"
          >
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Quality Assurance</h2>
              <p className="text-gray-700 max-w-3xl mx-auto">
                All our products undergo rigorous quality control processes to ensure they meet international standards 
                for food safety and quality. From farm to fork, we maintain the highest standards throughout our value chain.
              </p>
              <div className="flex justify-center mt-6 space-x-8">
                <div className="text-center">
                  <i className="fas fa-certificate text-tev-green text-3xl mb-2"></i>
                  <p className="text-gray-700">Certified Quality</p>
                </div>
                <div className="text-center">
                  <i className="fas fa-shield-alt text-tev-green text-3xl mb-2"></i>
                  <p className="text-gray-700">Food Safety</p>
                </div>
                <div className="text-center">
                  <i className="fas fa-globe text-tev-green text-3xl mb-2"></i>
                  <p className="text-gray-700">Global Standards</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
